import isJSON from "validator/es/lib/isJSON";
import * as JsonLoaderWorker from "worker-loader?name=static/[name].[contenthash:4].js!./json-loader.worker";

import { PromiseLruCache } from "@formant/core/common/PromiseLruCache";
import { range } from "@formant/core/common/range";

import { previewLength } from "./JsonLoaderConstants";

const workerCount = 4;

type JsonPrimitive = string | number | boolean | null;

interface IJsonMap {
    [member: string]: JsonPrimitive | IJsonArray | IJsonMap;
}

interface IJsonArray extends Array<JsonPrimitive | IJsonArray | IJsonMap> {}

export type Json = IJsonMap | IJsonArray | JsonPrimitive;

export interface IJsonLoaderResult {
    json: Json;
    preview: string;
    length: number;
}

export class JsonLoader {
    private static references: number = 0;
    private static instance: JsonLoader | null = null;

    public static get(): JsonLoader {
        JsonLoader.references++;
        if (JsonLoader.instance) {
            return JsonLoader.instance;
        }
        JsonLoader.instance = new JsonLoader();
        return JsonLoader.instance;
    }

    public static release(loader: JsonLoader) {
        if (JsonLoader.instance !== loader) {
            console.error("Releasing an unallocated/unowned loader!");
            return;
        }

        JsonLoader.references--;
        if (JsonLoader.references > 0) {
            return;
        }

        JsonLoader.instance.workers?.forEach(_ => _.terminate());
        JsonLoader.instance.workers = undefined;
        JsonLoader.instance.cache.clear();
        JsonLoader.instance = null;
        JsonLoader.references = 0;
    }

    private workers: Worker[] | undefined;
    private cache = new PromiseLruCache<string, IJsonLoaderResult>({
        name: "JsonLoader-cache",
        max: 5000,
    });
    private nextWorkerIndex: number = -1;
    private resolveMap: {
        [url: string]: (
            value: IJsonLoaderResult | PromiseLike<IJsonLoaderResult>,
        ) => void;
    } = {};
    private rejectMap: {
        [url: string]: (reason?: any) => void;
    } = {};

    private constructor() {}

    public load(unloaded: string): Promise<IJsonLoaderResult> {
        if (isJSON(unloaded)) {
            return new Promise<IJsonLoaderResult>((resolve, _) => {
                resolve({
                    json: JSON.parse(unloaded),
                    preview: unloaded.substring(0, previewLength),
                    length: unloaded.length,
                });
            });
        }

        const cached = this.cache.get(unloaded);
        if (cached) {
            return cached;
        }

        const worker = this.nextWorker();
        const loaded = new Promise<IJsonLoaderResult>((resolve, reject) => {
            this.resolveMap[unloaded] = resolve;
            this.rejectMap[unloaded] = reject;
        });
        this.cache.set(unloaded, loaded);
        worker.postMessage(unloaded);
        return loaded;
    }

    private nextWorker() {
        if (!this.workers) {
            this.workers = range(0, workerCount).map(_ => {
                const worker = new JsonLoaderWorker();
                worker.onmessage = ({ data }: any) => {
                    const { url } = data;
                    if (!url) {
                        return;
                    }
                    const reject = this.rejectMap[url];
                    const resolve = this.resolveMap[url];
                    delete this.rejectMap[url];
                    delete this.resolveMap[url];
                    if (!reject || !resolve) {
                        return;
                    }
                    if (data.error) {
                        const error = new Error(
                            `Worker failure: ${data.error}`,
                        );
                        (error as any).cause = data.error;
                        reject(error);
                        return;
                    }
                    resolve({
                        json: data.json,
                        preview: data.preview,
                        length: data.length,
                    });
                };
                return worker;
            });
        }
        this.nextWorkerIndex = (this.nextWorkerIndex + 1) % workerCount;
        return this.workers[this.nextWorkerIndex];
    }
}
